import { useState, useEffect, useCallback, useRef } from "react";
import { getAgencyTabCounts, getAgencyTrustStats } from "../../services/api";

// =====================================================================
// My Agency V2 shell data (wave-2 follow-up, F-1/F-2/F-3; BE PR #170).
//
// Lives OUTSIDE useAgencyProfile on purpose (that hook is shared and
// untouchable per the follow-up brief) — additive page-shell state only.
//
// One tab-counts fetch serves both the CMP-TABS count pills (F-3) and
// the board-1a/1l page subtitle "<name> · N members" (F-2); the trust
// stats (F-1) are their own endpoint. Both loads are FAIL-SILENT, same
// tolerance as the page's roster loads: an errored request leaves its
// slot null and the page hides the dependent element (subtitle / pills
// fall back to derived counts / stats row hidden) — never a blocker.
//
// BE contract notes (verbatim from the wave-2 brief):
// - `staff` is null when the caller cannot see the staff board (flag
//   off OR non-admin) → render NO staff pill then (not 0).
// - `players` is role-scoped exactly like the players board.
// - `member_count` == agents count.
// =====================================================================
const useAgencyShellData = () => {
  const [shellCounts, setShellCounts] = useState(null);
  const [trustStats, setTrustStats] = useState(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Exposed so the page can refresh counts at its existing roster-change
  // points (agent removed / invite sent / partner wall changed).
  const refreshShellCounts = useCallback(async () => {
    try {
      const res = await getAgencyTabCounts();
      const data = res?.data?.data;
      if (mountedRef.current && data && typeof data === "object") {
        setShellCounts(data);
      }
    } catch (e) {
      // Fail silent — pills fall back to the derived per-tab counts.
    }
  }, []);

  useEffect(() => {
    refreshShellCounts();
    (async () => {
      try {
        const res = await getAgencyTrustStats();
        const data = res?.data?.data;
        if (mountedRef.current && data && typeof data === "object") {
          setTrustStats(data);
        }
      } catch (e) {
        // Fail silent — the trust-stats row simply doesn't render.
      }
    })();
  }, [refreshShellCounts]);

  return { shellCounts, trustStats, refreshShellCounts };
};

export default useAgencyShellData;
